// ═══════════════════════════════════════════════════════════════
// BULLETIN BOARD IMAGE — Unity shows the 3D bulletin board as an
// image, so the calendar modal (month, today, events and the room's
// notes) is drawn onto a 16:9 canvas here.
// ═══════════════════════════════════════════════════════════════

import { forEachRoom } from "./worldJson";
import {
  formatWhen, isBulletinNote, isCalendarEvent, isDateKey, isPlannerItem, notesForRoom, toDateKey,
} from "./plannerItems";
import { isGhostEvent } from "./lumenCalendar";

export const BOARD_WIDTH = 1600;
export const BOARD_HEIGHT = 900;
const RENDER_VERSION = 4;

const SIDEBAR_W = 290;
const HEADER_H = 96;
const CORK_H = 236;
const GRID_PAD = 18;
const WEEKDAY_H = 30;
const NOTE_W = 158;
const NOTE_H = 116;
const NOTE_GAP = 18;

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const NOTE_COLORS = [
  { bg: "#FEF3C7", pin: "#F59E0B" },
  { bg: "#DBEAFE", pin: "#3B82F6" },
  { bg: "#DCFCE7", pin: "#22C55E" },
  { bg: "#FCE7F3", pin: "#EC4899" },
  { bg: "#EDE9FE", pin: "#8B5CF6" },
  { bg: "#FFEDD5", pin: "#F97316" },
];
const ROTATE = [-2, 1.5, -1, 2, -1.5, 1];
const PLACEHOLDER_NOTES = ["Ask Lumen to add todos here", "or mention action items in chat", "they'll pin here automatically"];
const FONT_STACK = '"DM Sans", "Helvetica Neue", Arial, sans-serif';

const font = (weight, size) => `${weight} ${size}px ${FONT_STACK}`;

const shortTime = (time) => {
  const [h, m] = time.split(":").map(Number);
  const suffix = h < 12 ? "am" : "pm";
  const hour = h % 12 === 0 ? 12 : h % 12;
  return m === 0 ? `${hour}${suffix}` : `${hour}:${String(m).padStart(2, "0")}${suffix}`;
};

const parseKey = (key) => {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(y, m - 1, d);
};

/** The weeks the month view shows, Sunday first, like react-big-calendar's month view. */
export function monthGrid(todayKey) {
  const today = parseKey(todayKey);
  const first = new Date(today.getFullYear(), today.getMonth(), 1);
  const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
  const weeks = Math.ceil((first.getDay() + daysInMonth) / 7);
  const days = [];
  for (let i = 0; i < weeks * 7; i++) {
    const day = new Date(today.getFullYear(), today.getMonth(), 1 - first.getDay() + i);
    days.push({ key: toDateKey(day), date: day.getDate(), inMonth: day.getMonth() === today.getMonth() });
  }
  return { month: today.getMonth(), year: today.getFullYear(), weeks, days };
}

const byStart = (a, b) => `${a.date}${a.start_time}`.localeCompare(`${b.date}${b.start_time}`);

/** Everything the image shows for one room on one day. */
export function boardContent(surfaceItems, roomId, todayKey) {
  const grid = monthGrid(todayKey);
  const first = grid.days[0].key;
  const last = grid.days[grid.days.length - 1].key;
  const events = (surfaceItems || [])
    .filter((e) => isCalendarEvent(e) && e.date >= first && e.date <= last)
    .sort(byStart);
  const notes = notesForRoom(surfaceItems, roomId);
  const upcoming = notes
    .filter((n) => isDateKey(n.date) && !n.done)
    .sort((a, b) => `${a.date}${a.time || ""}`.localeCompare(`${b.date}${b.time || ""}`))
    .slice(0, 3);
  return { todayKey, grid, events, todaysEvents: events.filter((e) => e.date === todayKey), notes, upcoming };
}

/** Changes whenever the board would look different. */
export function boardSignature(content) {
  const text = JSON.stringify([
    RENDER_VERSION,
    content.todayKey,
    content.events.map((e) => [e.date, e.start_time, e.end_time, e.title, isGhostEvent(e)]),
    content.notes.map((n) => [n.content || n.title || "", n.date || null, n.time || null, !!n.done]),
  ]);
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return `v${RENDER_VERSION}-${content.todayKey}-${(hash >>> 0).toString(16)}`;
}

const boardItemFor = (items, roomId) =>
  (items || []).find((item) =>
    item?.room_id === roomId && item.surface === "bulletin_board" &&
    !isPlannerItem(item) && !isBulletinNote(item) && item.image_path);

/** The room the user is in, when its saved board image is out of date. Other rooms catch up when visited. */
export function boardsToRefresh(surfaceItems, districts, roomId, todayKey) {
  if (!roomId || !isDateKey(todayKey)) return [];
  let inWorld = false;
  forEachRoom(districts || [], (room) => { if (room?.id === roomId) inWorld = true; });
  if (!inWorld) return [];
  const content = boardContent(surfaceItems, roomId, todayKey);
  const signature = boardSignature(content);
  const board = boardItemFor(surfaceItems, roomId);
  if (board?.notes_signature === signature) return [];
  return [{ roomId, content, signature, previousPath: board?.image_path || null }];
}

function wrapText(text, maxWidth, maxLines, measure) {
  const words = String(text || "").trim().split(/\s+/).filter(Boolean);
  const lines = [];
  let line = "";
  const pushWord = (word) => {
    let rest = word;
    while (measure(rest) > maxWidth && rest.length > 1) {
      let cut = rest.length - 1;
      while (cut > 1 && measure(rest.slice(0, cut)) > maxWidth) cut--;
      lines.push(rest.slice(0, cut));
      rest = rest.slice(cut);
    }
    return rest;
  };
  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (measure(candidate) <= maxWidth) { line = candidate; continue; }
    if (line) lines.push(line);
    line = pushWord(word);
  }
  if (line) lines.push(line);
  if (lines.length <= maxLines) return lines;
  const kept = lines.slice(0, maxLines);
  let last = kept[maxLines - 1];
  while (last && measure(`${last}…`) > maxWidth) last = last.slice(0, -1);
  kept[maxLines - 1] = `${last.trimEnd()}…`;
  return kept;
}

const fitText = (text, maxWidth, measure) => wrapText(text, maxWidth, 1, measure)[0] || "";

/** Where everything goes. `measure(text, cssFont)` returns a width in px. */
export function layoutBoard(content, measure) {
  const { grid } = content;
  const gridX = SIDEBAR_W + GRID_PAD;
  const gridY = HEADER_H + 12;
  const gridW = BOARD_WIDTH - gridX - GRID_PAD;
  const gridH = BOARD_HEIGHT - CORK_H - gridY - GRID_PAD;
  const cellW = gridW / 7;
  const cellH = (gridH - WEEKDAY_H) / grid.weeks;
  const chipH = 22;
  const chipGap = 3;
  const chipTop = 26;
  const chipRoom = Math.max(0, Math.floor((cellH - chipTop - 4) / (chipH + chipGap)));
  const chipFont = font(500, 13);

  const cells = grid.days.map((day, i) => {
    const x = gridX + (i % 7) * cellW;
    const y = gridY + WEEKDAY_H + Math.floor(i / 7) * cellH;
    const dayEvents = content.events.filter((e) => e.date === day.key);
    const fits = dayEvents.length <= chipRoom ? dayEvents.length : Math.max(0, chipRoom - 1);
    const chips = dayEvents.slice(0, fits).map((e, n) => ({
      x: x + 3, y: y + chipTop + n * (chipH + chipGap), width: cellW - 6, height: chipH,
      text: fitText(`${shortTime(e.start_time)}  ${e.title}`, cellW - 16, (t) => measure(t, chipFont)),
      ghost: isGhostEvent(e),
    }));
    const more = dayEvents.length - fits;
    return { ...day, x, y, width: cellW, height: cellH, today: day.key === content.todayKey, chips,
      more: more > 0 ? { text: `+${more} more`, x: x + 8, y: y + chipTop + fits * (chipH + chipGap) } : null };
  });

  const corkY = BOARD_HEIGHT - CORK_H;
  const slots = Math.floor((BOARD_WIDTH - 56 + NOTE_GAP) / (NOTE_W + NOTE_GAP));
  const source = content.notes.length
    ? content.notes
    : PLACEHOLDER_NOTES.map((text) => ({ content: text, placeholder: true }));
  const shown = source.length > slots ? source.slice(0, slots - 1) : source;
  const noteFont = font(500, 15);
  const notes = shown.map((note, i) => {
    const when = note.placeholder ? null : formatWhen(note);
    const footerLines = (when ? 1 : 0) + (note.done ? 1 : 0);
    const lines = wrapText(note.content || note.title, NOTE_W - 28, 4 - footerLines,
      (t) => measure(t, noteFont));
    return {
      x: 34 + i * (NOTE_W + NOTE_GAP), y: corkY + 78, width: NOTE_W, height: NOTE_H,
      color: NOTE_COLORS[i % NOTE_COLORS.length], rotate: ROTATE[i % ROTATE.length],
      lines, when, done: !!note.done, placeholder: !!note.placeholder,
    };
  });
  const moreNotes = source.length - shown.length;

  const sidebarFont = font(600, 16);
  return {
    header: { month: MONTHS[grid.month], year: String(grid.year) },
    weekdays: WEEKDAYS.map((name, i) => ({ name, x: gridX + i * cellW, y: gridY, width: cellW, height: WEEKDAY_H })),
    cells,
    today: parseKey(content.todayKey),
    todaysEvents: content.todaysEvents.slice(0, 4).map((e) => ({
      title: fitText(e.title, SIDEBAR_W - 70, (t) => measure(t, sidebarFont)), time: `${e.start_time} – ${e.end_time}`,
      ghost: isGhostEvent(e),
    })),
    moreToday: Math.max(0, content.todaysEvents.length - 4),
    upcoming: content.upcoming.map((n) => ({
      text: fitText(n.content || n.title, SIDEBAR_W - 56, (t) => measure(t, sidebarFont)), when: formatWhen(n),
    })),
    corkY,
    notes,
    moreNotes: moreNotes > 0
      ? { text: `+${moreNotes} more`, x: 34 + shown.length * (NOTE_W + NOTE_GAP), y: corkY + 78, width: NOTE_W, height: NOTE_H }
      : null,
  };
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function drawSidebar(ctx, layout) {
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, SIDEBAR_W, layout.corkY);
  ctx.fillStyle = "#1E293B";
  ctx.fillRect(0, 0, SIDEBAR_W, 236);
  ctx.textBaseline = "top";
  ctx.textAlign = "left";
  ctx.fillStyle = "rgba(255,255,255,0.5)";
  ctx.font = font(700, 14);
  ctx.fillText("TODAY", 24, 30);
  ctx.fillStyle = "#FFFFFF";
  ctx.font = font(800, 76);
  ctx.fillText(String(layout.today.getDate()), 22, 56);
  ctx.fillStyle = "rgba(255,255,255,0.85)";
  ctx.font = font(600, 20);
  ctx.fillText(layout.today.toLocaleDateString("en-US", { weekday: "long" }), 24, 148);
  ctx.fillStyle = "rgba(255,255,255,0.5)";
  ctx.font = font(400, 16);
  ctx.fillText(layout.today.toLocaleDateString("en-US", { month: "short", year: "numeric" }), 24, 178);

  let y = 262;
  ctx.fillStyle = "#94A3B8";
  ctx.font = font(700, 13);
  ctx.fillText("TODAY'S EVENTS", 22, y);
  y += 28;
  if (!layout.todaysEvents.length) {
    ctx.font = `italic ${font(400, 15)}`;
    ctx.fillText("Nothing scheduled yet.", 22, y);
    y += 34;
  }
  for (const e of layout.todaysEvents) {
    ctx.fillStyle = "#F8FAFC";
    roundRect(ctx, 18, y, SIDEBAR_W - 36, 56, 9);
    ctx.fill();
    ctx.fillStyle = e.ghost ? "#CBD5E1" : "#4DB8B5";
    ctx.fillRect(18, y, 4, 56);
    ctx.fillStyle = e.ghost ? "#64748B" : "#1E293B";
    ctx.font = font(600, 16);
    ctx.fillText(e.title, 32, y + 9);
    ctx.fillStyle = "#94A3B8";
    ctx.font = font(400, 13);
    ctx.fillText(e.time, 32, y + 33);
    y += 64;
  }
  if (layout.moreToday) {
    ctx.fillStyle = "#94A3B8";
    ctx.font = font(600, 13);
    ctx.fillText(`+${layout.moreToday} more`, 22, y);
    y += 26;
  }
  if (layout.upcoming.length && y < layout.corkY - 60) {
    y += 8;
    ctx.fillStyle = "#94A3B8";
    ctx.font = font(700, 13);
    ctx.fillText("COMING UP", 22, y);
    y += 26;
    for (const n of layout.upcoming) {
      if (y > layout.corkY - 44) break;
      ctx.fillStyle = "#334155";
      ctx.font = font(600, 16);
      ctx.fillText(n.text, 22, y);
      ctx.fillStyle = "#4DB8B5";
      ctx.font = font(500, 13);
      ctx.fillText(n.when, 22, y + 22);
      y += 48;
    }
  }
  ctx.fillStyle = "rgba(0,0,0,0.06)";
  ctx.fillRect(SIDEBAR_W - 1, 0, 1, layout.corkY);
}

function drawCalendar(ctx, layout) {
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(SIDEBAR_W, 0, BOARD_WIDTH - SIDEBAR_W, layout.corkY);
  ctx.textBaseline = "middle";
  ctx.fillStyle = "#64748B";
  ctx.font = font(400, 44);
  ctx.textAlign = "center";
  ctx.fillText("‹", SIDEBAR_W + 44, HEADER_H / 2 - 2);
  ctx.fillText("›", SIDEBAR_W + 94, HEADER_H / 2 - 2);
  ctx.textAlign = "left";
  ctx.fillStyle = "#1E293B";
  ctx.font = font(700, 30);
  ctx.fillText(layout.header.month, SIDEBAR_W + 130, HEADER_H / 2);
  const monthW = ctx.measureText(layout.header.month).width;
  ctx.fillStyle = "#94A3B8";
  ctx.font = font(400, 22);
  ctx.fillText(layout.header.year, SIDEBAR_W + 130 + monthW + 12, HEADER_H / 2 + 2);
  ctx.fillStyle = "rgba(0,0,0,0.06)";
  ctx.fillRect(SIDEBAR_W, HEADER_H - 1, BOARD_WIDTH - SIDEBAR_W, 1);

  ctx.strokeStyle = "#DDDDDD";
  ctx.lineWidth = 1;
  for (const w of layout.weekdays) {
    ctx.strokeRect(w.x + 0.5, w.y + 0.5, w.width, w.height);
    ctx.fillStyle = "#1E293B";
    ctx.font = font(700, 14);
    ctx.textAlign = "center";
    ctx.fillText(w.name, w.x + w.width / 2, w.y + w.height / 2);
  }
  for (const c of layout.cells) {
    ctx.fillStyle = c.today ? "#EAF6FF" : c.inMonth ? "#FFFFFF" : "#E6E6E6";
    ctx.fillRect(c.x, c.y, c.width, c.height);
    ctx.strokeRect(c.x + 0.5, c.y + 0.5, c.width, c.height);
    ctx.textAlign = "right";
    ctx.textBaseline = "top";
    ctx.fillStyle = c.inMonth ? "#1E293B" : "#999999";
    ctx.font = font(c.today ? 800 : 500, 15);
    ctx.fillText(String(c.date), c.x + c.width - 8, c.y + 6);
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    for (const chip of c.chips) {
      ctx.fillStyle = chip.ghost ? "#E2E8F0" : "#3174AD";
      roundRect(ctx, chip.x, chip.y, chip.width, chip.height, 5);
      ctx.fill();
      if (chip.ghost) {
        ctx.setLineDash([4, 3]);
        ctx.strokeStyle = "#94A3B8";
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.strokeStyle = "#DDDDDD";
      }
      ctx.fillStyle = chip.ghost ? "#64748B" : "#FFFFFF";
      ctx.font = font(500, 13);
      ctx.fillText(chip.text, chip.x + 6, chip.y + chip.height / 2 + 1);
    }
    if (c.more) {
      ctx.fillStyle = "#3174AD";
      ctx.font = font(700, 13);
      ctx.fillText(c.more.text, c.more.x, c.more.y + 10);
    }
  }
}

function drawCork(ctx, layout) {
  const y0 = layout.corkY;
  const gradient = ctx.createLinearGradient(0, y0, BOARD_WIDTH, BOARD_HEIGHT);
  gradient.addColorStop(0, "#C4956A");
  gradient.addColorStop(0.4, "#A67C52");
  gradient.addColorStop(0.7, "#B8865E");
  gradient.addColorStop(1, "#C4956A");
  ctx.fillStyle = gradient;
  ctx.fillRect(0, y0, BOARD_WIDTH, CORK_H);
  ctx.strokeStyle = "rgba(0,0,0,0.035)";
  ctx.lineWidth = 2;
  for (let x = -CORK_H; x < BOARD_WIDTH; x += 6) {
    ctx.beginPath();
    ctx.moveTo(x, BOARD_HEIGHT);
    ctx.lineTo(x + CORK_H, y0);
    ctx.stroke();
  }
  ctx.fillStyle = "#8B6340";
  ctx.fillRect(0, y0, BOARD_WIDTH, 5);
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillStyle = "rgba(255,255,255,0.85)";
  ctx.font = font(700, 20);
  ctx.fillText("📌 BULLETIN BOARD", 34, y0 + 26);

  for (const note of layout.notes) {
    ctx.save();
    ctx.translate(note.x + note.width / 2, note.y + note.height / 2);
    ctx.rotate((note.rotate * Math.PI) / 180);
    ctx.translate(-note.width / 2, -note.height / 2);
    ctx.globalAlpha = note.placeholder ? 0.7 : note.done ? 0.55 : 1;
    ctx.shadowColor = "rgba(0,0,0,0.25)";
    ctx.shadowBlur = 10;
    ctx.shadowOffsetX = 3;
    ctx.shadowOffsetY = 4;
    ctx.fillStyle = note.color.bg;
    roundRect(ctx, 0, 0, note.width, note.height, 5);
    ctx.fill();
    ctx.shadowColor = "transparent";
    ctx.fillStyle = note.color.pin;
    ctx.beginPath();
    ctx.arc(note.width / 2, 2, 7, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = note.placeholder ? "#64748B" : "#1E293B";
    ctx.font = note.placeholder ? `italic ${font(400, 14)}` : font(500, 15);
    note.lines.forEach((line, i) => {
      const ly = 22 + i * 20;
      ctx.fillText(line, 14, ly);
      if (note.done) ctx.fillRect(14, ly + 9, ctx.measureText(line).width, 1.5);
    });
    const footerY = note.height - 26;
    ctx.font = font(700, 13);
    if (note.when) {
      ctx.fillStyle = "#0F766E";
      ctx.fillText(note.when, 14, note.done ? footerY - 18 : footerY);
    }
    if (note.done) {
      ctx.fillStyle = "#16A34A";
      ctx.fillText("✓ done", 14, footerY);
    }
    ctx.restore();
  }
  if (layout.moreNotes) {
    const m = layout.moreNotes;
    ctx.strokeStyle = "rgba(255,255,255,0.6)";
    ctx.setLineDash([6, 5]);
    ctx.lineWidth = 2;
    roundRect(ctx, m.x, m.y, m.width, m.height, 6);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = "rgba(255,255,255,0.9)";
    ctx.font = font(700, 18);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(m.text, m.x + m.width / 2, m.y + m.height / 2);
  }
}

/** The room's calendar and notes as a JPEG data URL, sized for the 3D bulletin board. */
export async function renderBulletinBoardImage(content) {
  if (typeof document === "undefined") throw new Error("renderBulletinBoardImage needs a browser");
  try { await document.fonts?.ready; } catch { /* draw with the fallback font */ }
  const canvas = document.createElement("canvas");
  canvas.width = BOARD_WIDTH;
  canvas.height = BOARD_HEIGHT;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("canvas is not available");
  const measure = (text, cssFont) => {
    ctx.font = cssFont;
    return ctx.measureText(text).width;
  };
  const layout = layoutBoard(content, measure);
  drawSidebar(ctx, layout);
  drawCalendar(ctx, layout);
  drawCork(ctx, layout);
  return canvas.toDataURL("image/jpeg", 0.9);
}
